import { dirname, basename } from 'node:path';
import { rm } from 'node:fs/promises';
import { generateBlocksWidget, generateBlocksWidgetPanel } from './generate.js';
import type {
  BlocksWidget,
  CLIBlocksWidget,
  CLIBlocksWidgetsPanel,
} from './types.js';

function mapById<T>(arr: Array<{ id: string } & T>) {
  return new Map(arr.map((item) => [item.id, item]));
}

function getArrayChanges<T, V>({
  existingComponents,
  targetComponents,
}: {
  existingComponents: Array<{ id: string } & T>;
  targetComponents: Array<{ id: string } & V>;
}) {
  const getId = (component: { id: string }) => component.id;

  const existingIdSet = new Set(existingComponents.map(getId));
  const targetIdSet = new Set(targetComponents.map(getId));

  return {
    added: targetComponents.filter((x) => !existingIdSet.has(x.id)),
    removed: existingComponents.filter((x) => !targetIdSet.has(x.id)),
    unchanged: targetComponents.filter((x) => existingIdSet.has(x.id)),
  };
}

async function syncBlocksComponents(
  projectFolder: string,
  targetWidgets: BlocksWidget[],
  existingWidgets: CLIBlocksWidget[],
  existingPanels: CLIBlocksWidgetsPanel[]
) {
  function getSyncWidgetActions() {
    const { added, removed } = getArrayChanges({
      existingComponents: existingWidgets,
      targetComponents: targetWidgets,
    });

    const addActions = added.map((widget) => {
      return generateBlocksWidget(projectFolder, widget.id, widget.name);
    });

    const removeActions = removed.map((widget) => {
      return rm(dirname(widget.relativePath), {
        recursive: true,
      });
    });

    return [...addActions, ...removeActions];
  }

  function getSyncWidgetPanelsActions() {
    const actions = [];

    for (const widget of targetWidgets) {
      const newWidgetPanels = widget.panels ?? [];
      const oldWidgetPanels = existingPanels.filter(
        (panel) => panel.widgetId === widget.id
      );

      const { added, removed } = getArrayChanges({
        existingComponents: oldWidgetPanels,
        targetComponents: newWidgetPanels,
      });

      const addActions = added.map((panel) => {
        const existingWidget = existingWidgetsById.get(widget.id);
        return generateBlocksWidgetPanel(
          projectFolder,
          panel.id,
          panel.name,
          existingWidget
            ? basename(dirname(existingWidget.relativePath))
            : widget.name
        );
      });

      actions.push(...addActions);

      const removeActions = removed.map((panel) => {
        return rm(dirname(panel.relativePath), {
          recursive: true,
        });
      });

      actions.push(...removeActions);
    }

    return actions;
  }

  const existingWidgetsById = mapById(existingWidgets);

  const widgetActions = getSyncWidgetActions();
  const panelActions = getSyncWidgetPanelsActions();

  // Panels are nested inside the blocks widget folders,
  // so we need to first sync all the blocks widget folder and afterwards sync the panels
  await Promise.all(widgetActions);
  await Promise.all(panelActions);
}

export default syncBlocksComponents;
